import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import { SnackBarService } from './snack-service.service';

export const LAST_SEEN_BUILD_KEY = 'ri.lastSeenBuild';
export const LAST_SEEN_CODENAME_KEY = 'ri.lastSeenCodename';

const DATE_LOCALES: Record<string, string> = {
  fi: 'fi-FI',
  en: 'en-GB',
  gb: 'en-GB',
  ee: 'et-EE',
};

export interface BuildInfo {
  appVersion?: string;
  buildSha?: string;
  buildTime?: string;
  /** Optional name of the current major release; empty/absent = none is shown anywhere. */
  codename?: string;
}

/** Everything the about dialog shows and the "copy for support" button copies. */
export interface AboutInfo {
  codename: string;
  /** Update date for display, no time. */
  updated: string;
  appVersion: string;
  shortSha: string;
  buildTime: string;
  language: string;
  userAgent: string;
  swEnabled: boolean;
}

/** Shows the app version in the footer and announces an update once per build per browser. */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  constructor(
    private translate: TranslateService,
    private snackBar: SnackBarService,
    private logger: LoggerService
  ) {}

  /**
   * Footer line, re-emitted when the UI language changes: "RaksaInfo [codename] · päivitetty <date>".
   * Never contains a hash or run number. Emits '' when the build has no usable date.
   */
  readonly versionLine$: Observable<string> = this.translate.onLangChange.pipe(
    startWith(null),
    switchMap(() => {
      const { buildTime } = this.buildInfo();
      const date = this.formatBuildTime(buildTime, this.translate.getCurrentLang() ?? undefined);
      if (!date) return of('');
      const codename = this.codename();
      return this.translate
        .get(codename ? 'FOOTER.UPDATED_ON_CODENAME' : 'FOOTER.UPDATED_ON', { date, codename })
        .pipe(map((text: string) => text));
    })
  );

  /** Codename of the running release, or '' when none is set. */
  codename(): string {
    return (this.buildInfo().codename ?? '').trim();
  }

  /** Year for the footer copyright: the build year, else the current year. */
  copyrightYear(): number {
    const t = new Date(this.buildInfo().buildTime ?? '');
    return isNaN(t.getTime()) ? new Date().getFullYear() : t.getFullYear();
  }

  /** Data for the about dialog. */
  aboutInfo(swEnabled: boolean): AboutInfo {
    const info = this.buildInfo();
    const lang = this.translate.getCurrentLang() ?? undefined;
    return {
      codename: this.codename(),
      updated: this.formatBuildTime(info.buildTime, lang),
      appVersion: (info.appVersion ?? '').trim(),
      shortSha: (info.buildSha ?? '').trim().slice(0, 8),
      buildTime: info.buildTime ?? '',
      language: lang ?? '',
      userAgent: navigator.userAgent,
      swEnabled,
    };
  }

  /** Plain-text block for the "copy details for support" button. */
  supportText(about: AboutInfo): string {
    return [
      `RaksaInfo${about.codename ? ' ' + about.codename : ''}`,
      `Version: ${about.appVersion || '-'}`,
      `Build: ${about.shortSha || '-'} (${about.buildTime || '-'})`,
      `Updated: ${about.updated || '-'}`,
      `Language: ${about.language || '-'}`,
      `Service worker: ${about.swEnabled ? 'on' : 'off'}`,
      `Browser: ${about.userAgent}`,
    ].join('\n');
  }

  /** Formats the build date (no time) in the user's local time and the UI language; '' if missing or invalid. */
  formatBuildTime(buildTime: string | undefined, lang: string | undefined): string {
    if (!buildTime) return '';
    const date = new Date(buildTime);
    if (isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(DATE_LOCALES[lang ?? ''] ?? 'fi-FI', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      }).format(date);
    } catch {
      return '';
    }
  }

  /**
   * Call once on app start (main app only). Ordinary deploys are silent: the user already is on
   * the new build and a version string would tell them nothing. Only when a *codename* is set
   * and differs from the one this browser last saw (a major update) is a short card shown:
   * "RaksaInfo <codename> on nyt käytössä" with a "Mitä uutta?" action.
   */
  announceUpdateIfNeeded(onWhatsNew?: () => void): void {
    const info = this.buildInfo();
    const current = (info.buildSha || info.appVersion || '').trim();
    if (!current) return;
    const codename = this.codename();

    let previous: string | null = null;
    let previousCodename: string | null = null;
    try {
      previous = localStorage.getItem(LAST_SEEN_BUILD_KEY);
      previousCodename = localStorage.getItem(LAST_SEEN_CODENAME_KEY);
      if (previous !== current) {
        localStorage.setItem(LAST_SEEN_BUILD_KEY, current);
      }
      if (codename && previousCodename !== codename) {
        localStorage.setItem(LAST_SEEN_CODENAME_KEY, codename);
      }
    } catch (e) {
      this.logger.debug('AppVersionService', 'localStorage unavailable', e);
      return;
    }

    // Brand-new browser (nothing stored): stay silent. No codename: nothing to announce.
    if (!previous || !codename || previousCodename === codename) return;

    // translate.get waits until the translations are loaded
    this.translate
      .get(['APP_UPDATE.NOW_RUNNING', 'APP_UPDATE.WHATS_NEW'], { codename })
      .pipe(take(1))
      .subscribe((texts: Record<string, string>) => {
        const ref = this.snackBar.info(texts['APP_UPDATE.NOW_RUNNING'], {
          action: texts['APP_UPDATE.WHATS_NEW'],
          duration: 8000,
          verticalPosition: 'bottom',
        });
        ref.onAction().pipe(take(1)).subscribe(() => onWhatsNew?.());
      });
  }

  /** Overridable seam for tests. */
  protected buildInfo(): BuildInfo {
    return environment as BuildInfo;
  }
}
